import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Typography, Avatar, Chip } from "@mui/material";
import { FaFire, FaBolt } from "react-icons/fa";
import img3 from "../../images/sega-coin.svg";
import { getAllData } from "../../Utility/API";
import { endPoints } from "../../constant/Environment";

// Mock data matching API response format
const mockHighRollers = [
  { id: 1, name: "Jackson", gameName: "Mega Slots", betamount: 100, winamount: 12480, created_on: new Date().toISOString() },
  { id: 2, name: "Marina", gameName: "Blackjack Pro", betamount: 250, winamount: 9230, created_on: new Date(Date.now() - 12000).toISOString() },
  { id: 3, name: "Alexander", gameName: "Lucky Roulette", betamount: 150, winamount: 8120, created_on: new Date(Date.now() - 34000).toISOString() },
  { id: 4, name: "Sophia", gameName: "Dragon Spin", betamount: 200, winamount: 7560, created_on: new Date(Date.now() - 60000).toISOString() },
  { id: 5, name: "Oliver", gameName: "Mega Slots", betamount: 180, winamount: 6890, created_on: new Date(Date.now() - 65000).toISOString() },
  { id: 6, name: "Emily", gameName: "Poker Stars", betamount: 120, winamount: 5430, created_on: new Date(Date.now() - 120000).toISOString() },
  { id: 7, name: "Lucas", gameName: "Fortune Wheel", betamount: 90, winamount: 4870, created_on: new Date(Date.now() - 125000).toISOString() },
  { id: 8, name: "Avalon", gameName: "Lucky Roulette", betamount: 110, winamount: 4210, created_on: new Date(Date.now() - 180000).toISOString() },
  { id: 9, name: "Ethan", gameName: "Dragon Spin", betamount: 75, winamount: 3760, created_on: new Date(Date.now() - 240000).toISOString() },
  { id: 10, name: "Isabel", gameName: "Blackjack Pro", betamount: 85, winamount: 3120, created_on: new Date(Date.now() - 300000).toISOString() },
];

const AVATAR_COLORS = [
  "#e91e63", "#9c27b0", "#3f51b5", "#2196f3",
  "#00bcd4", "#009688", "#4caf50", "#ff5722",
  "#ff9800", "#795548", "#607d8b", "#f44336",
];

function formatWin(val) {
  if (val >= 1000000) return (val / 1000000).toFixed(1) + "M";
  if (val >= 1000) return (val / 1000).toFixed(1) + "K";
  return val.toFixed(2);
}

function getTimeAgo(timestamp) {
  const now = new Date();
  const created = new Date(timestamp);
  const diffMs = now - created;
  const diffSec = Math.floor(diffMs / 1000);
  
  if (diffSec < 10) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  return `${diffHour}h ago`;
}

function HighRollerRow({ player, index, currency }) {
  const { t } = useTranslation();
  const avatarColor = AVATAR_COLORS[(player.username.charCodeAt(0) - 65) % AVATAR_COLORS.length];
  const isHot = index < 3;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.2,
        borderRadius: 2,
        background: isHot
          ? "linear-gradient(90deg, rgba(255,159,0,0.10) 0%, rgba(255,87,34,0.05) 100%)"
          : "rgba(255,255,255,0.03)",
        border: isHot
          ? "1px solid rgba(255,159,0,0.25)"
          : "1px solid transparent",
        transition: "all 0.2s",
        "&:hover": {
          background: isHot
            ? "linear-gradient(90deg, rgba(255,159,0,0.16) 0%, rgba(255,87,34,0.10) 100%)"
            : "rgba(255,255,255,0.06)",
        },
      }}
    >
      <Box sx={{ width: 24, textAlign: "center", flexShrink: 0 }}>
        {isHot ? (
          <FaFire size={16} color="#FF9F00" />
        ) : (
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.4)" }}>
            #{index + 1}
          </Typography>
        )}
      </Box>

      <Avatar
        sx={{
          width: 36,
          height: 36,
          fontSize: 14,
          fontWeight: 700,
          background: avatarColor,
          flexShrink: 0,
        }}
      >
        {player.username.charAt(0)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: "#fff",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {player.username}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: "rgba(255,255,255,0.45)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {player.game}
          {/* • {player.timeAgo} */}
        </Typography>
      </Box>

      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>
          {t("leaderboard.highRollersBet", { amount: formatWin(player.bet) })}
          {/* {currency} */}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, justifyContent: "flex-end" }}>
          <FaBolt size={11} color="#FFD700" />
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#4eeccd" }}>
           {t("leaderboard.highRollersWin", { amount: formatWin(player.win) })}
           {/* {currency} */}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const HighRollers = () => {
  const { t } = useTranslation();
  const [highRollers, setHighRollers] = useState([]);
  const IsLoginData = useSelector((state) => state.auth.IsLoginData);
  const currency = IsLoginData?.currency || "USD";

  console.log("HighRollers component rendered with currency:", currency,IsLoginData);

  useEffect(() => {
    // Transform API data to component format
    const transformData = (data) => {
      return (data ?? []).map((item) => ({
        id: item.id,
        username: item.name || t("leaderboard.highRollersAnonymous"),
        game: item.gameName || t("leaderboard.highRollersUnknownGame"),
        bet: parseFloat((item.playAmount ?? 0).toFixed(2)),
        win: parseFloat((item.totalWinAmount ?? 0).toFixed(2)),
        // timeAgo: getTimeAgo(item.created_on),
      }));
    };

    const fetchTopWins = async () => {
      try {
        const response = await getAllData(endPoints.api.GET_TOPWINS);
        if (response?.status === "success" && response?.data?.length > 0) {
          setHighRollers(transformData(response.data));
        } else {
          // Use mock data if API returns no data
          setHighRollers(transformData(mockHighRollers));
        }
      } catch (err) {
        console.log("TopWins API error:", err);
        // Fallback to mock data on error
        setHighRollers(transformData(mockHighRollers));
      }
    };

    // Initial fetch
    fetchTopWins();

    // Poll every 5 seconds
    const interval = setInterval(fetchTopWins, 5000);

    return () => clearInterval(interval);
  }, [t]);

  return (
    <Box>
      {/* Live status strip */}
      <Box
        sx={{
          mx: 3,
          mt: 2,
          mb: 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          background: "linear-gradient(90deg, rgba(3,179,73,0.10) 0%, rgba(78,236,205,0.06) 100%)",
          border: "1px solid rgba(3,179,73,0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#03B349",
              boxShadow: "0 0 8px #03B349",
              animation: "pulse 1.4s infinite",
            }}
          />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>
            {t("leaderboard.highRollersLiveWins")}
          </Typography>
        </Box>
        <Chip
          label={t("leaderboard.highRollersUpdatingRealTime")}
          size="small"
          sx={{
            height: 20,
            fontSize: 10,
            fontWeight: 600,
            background: "rgba(255,255,255,0.05)",
            color: "rgba(255,255,255,0.5)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        />
      </Box>

      {/* Rows */}
      <Box sx={{ px: 2, pt: 1, pb: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
          {highRollers.map((player, i) => (
            <HighRollerRow key={player.id} player={player} index={i} currency={currency} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HighRollers;
